import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import PreviewFile from '$components/PreviewFile/PreviewFile';
import SimeraIframe from '$components/UI/SimeraIframe/SimeraIframe';
import { SearchCandidateOutput } from '$store/modules/candidates';
import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

export type ModalInfoType = {
  type: 'spk' | 'cv';
  candidateId: number;
  name: string;
};

const SearchCandidateModal = forwardRef(
  (
    {
      candidatesList,
      selectedRows,
      toggleSelectionHandler,
    }: // openAddToPositionModal,
    {
      candidatesList: SearchCandidateOutput['data'];
      selectedRows: { number: boolean } | {};
      toggleSelectionHandler: (
        candidateId: number,
        isAddition: boolean
      ) => void;
      // openAddToPositionModal: () => void;
    },
    ref: ForwardedRef<{
      setCandidateInfo: (info: ModalInfoType) => void;
    }> | null
  ) => {
    const [modalInfo, setModalInfo] = useState<ModalInfoType | null>(null);

    const nextCandidateIdAndName = useMemo(() => {
      if (!modalInfo?.candidateId || !candidatesList) return null;
      const currentCandidateIndex = candidatesList.findIndex(
        (singleCandidate) => singleCandidate.Id === modalInfo.candidateId
      );
      let resultCandidate;
      // If current candidate is the last one, return to the first candidate Id
      if (currentCandidateIndex + 1 >= candidatesList.length) {
        const [firstCandidate] = candidatesList;
        resultCandidate = firstCandidate;
      } else resultCandidate = candidatesList[currentCandidateIndex + 1];
      // Otherwise return the next candidate id
      return {
        candidateId: resultCandidate.Id,
        name: `${resultCandidate.User.FirstName || ''} ${
          resultCandidate.User.LastName || ''
        }`,
      };
    }, [candidatesList, modalInfo?.candidateId]);

    const previousCandidateIdAndName = useMemo(() => {
      if (!modalInfo?.candidateId || !candidatesList) return null;
      const currentCandidateIndex = candidatesList.findIndex(
        (singleCandidate) => singleCandidate.Id === modalInfo.candidateId
      );
      let resultCandidate;
      // If current candidate is the first one, return to the last candidate Id
      if (currentCandidateIndex === 0) resultCandidate = candidatesList?.at(-1);
      else resultCandidate = candidatesList[currentCandidateIndex - 1];
      // Otherwise return the previous candidate id
      return {
        candidateId: resultCandidate?.Id,
        name: `${resultCandidate?.User?.FirstName || ''} ${
          resultCandidate?.User?.LastName || ''
        }`,
      };
    }, [candidatesList, modalInfo?.candidateId]);

    const isAddToSelection = useMemo(() => {
      if (!modalInfo?.candidateId) return false;
      if (
        selectedRows[
          modalInfo.candidateId as unknown as keyof typeof selectedRows
        ] === undefined
      ) {
        return true;
      }
      return !selectedRows[
        modalInfo.candidateId as unknown as keyof typeof selectedRows
      ];
    }, [modalInfo?.candidateId, selectedRows]);

    // const hasSelectedCandidates = useMemo(() => {
    //   return Object.values(selectedRows).filter((value) => value).length;
    // }, [selectedRows]);

    const modalUrl = useMemo(() => {
      if (!modalInfo) return '';
      return modalInfo?.type === 'cv'
        ? candidatesList.find(
            (singleCandidate) => singleCandidate.Id === modalInfo.candidateId
          )?.Curriculum
        : `${import.meta.env.REACT_APP_SPK_URL}/spk/${modalInfo.candidateId}?hideNavbar=true&minimizeVideo=true`;
    }, [candidatesList, modalInfo]);

    const handleCloseModal = () => {
      setModalInfo(null);
    };

    useImperativeHandle(ref, () => ({
      setCandidateInfo: (candidateInfo: ModalInfoType) => {
        setModalInfo(candidateInfo);
      },
      closeModal: handleCloseModal,
    }));

    return (
      <ModalInTailwind isOpen={!!modalInfo} closeModal={handleCloseModal}>
        <div className="container max-w-screen max-h-screen p-8 overflow-hidden h-full flex flex-col gap-2">
          <div className="bg-white shadow rounded w-full p-4 flex flex-row justify-between">
            <div className="flex flex-row items-center justify-between gap-4">
              {candidatesList.length > 1 && (
                <>
                  <button
                    className="bg-internal-main text-white rounded px-2 py-1"
                    type="button"
                    onClick={() =>
                      setModalInfo(
                        (prev) =>
                          ({
                            ...prev,
                            ...previousCandidateIdAndName,
                          }) as ModalInfoType
                      )
                    }
                  >
                    <i className="pi pi-chevron-left" />
                    Previous Candidate
                  </button>
                  <button
                    className="bg-internal-main text-white rounded px-2 py-1"
                    type="button"
                    onClick={() =>
                      setModalInfo(
                        (prev) =>
                          ({
                            ...prev,
                            ...nextCandidateIdAndName,
                          }) as ModalInfoType
                      )
                    }
                  >
                    Next Candidate <i className="pi pi-chevron-right" />
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-col items-center">
              <span className="font-semibold text-lg">{modalInfo?.name}</span>
              <span className="text-xs">{`CANDIDATE ID: ${modalInfo?.candidateId}`}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              {modalInfo?.type === 'spk' && (
                <button
                  type="button"
                  className="bg-internal-link/80 text-white px-2 py-1 rounded"
                  onClick={() =>
                    setModalInfo(
                      (prev) => ({ ...prev, type: 'cv' }) as ModalInfoType
                    )
                  }
                >
                  <i className="pi pi-id-card mr-2" />
                  View CV
                </button>
              )}
              {modalInfo?.type === 'cv' && (
                <button
                  type="button"
                  className="bg-internal-link/80 text-white px-2 py-1 rounded"
                  onClick={() =>
                    setModalInfo(
                      (prev) => ({ ...prev, type: 'spk' }) as ModalInfoType
                    )
                  }
                >
                  <i className="pi pi-user mr-2" />
                  View SPK
                </button>
              )}
              {/* <button
                type="button"
                onClick={openAddToPositionModal}
                disabled={!hasSelectedCandidates}
                className="bg-internal-main disabled:bg-gray-400 disabled:cursor-not-allowed px-2 py-1 text-white rounded"
              >
                <i className="pi pi-plus" /> Add to a position
              </button> */}
              <button
                type="button"
                aria-label="Add to selection"
                title="Add to selection"
                className={`${
                  isAddToSelection
                    ? 'bg-spk-brightBlue text-white'
                    : 'bg-spk-brightRed/70 text-white '
                } px-2 py-1 rounded`}
                onClick={() =>
                  toggleSelectionHandler(
                    modalInfo?.candidateId as number,
                    isAddToSelection
                  )
                }
              >
                <i
                  className={`pi ${
                    isAddToSelection ? 'pi-user-plus' : 'pi-user-minus'
                  } mr-2`}
                />
                {isAddToSelection ? 'Add to' : 'Remove from'} selection
              </button>
              <button
                type="button"
                onClick={handleCloseModal}
                className="bg-slate-300 px-2 py-1 rounded"
              >
                Close <i className="pi pi-times ml-1" />
              </button>
            </div>
          </div>
          <div
            key={`${modalInfo?.candidateId}-${modalInfo?.type}`}
            className="overflow-scroll bg-white flex flex-col rounded max-h-full h-full relative"
          >
            {!modalUrl && (
              <div className="h-full flex flex-col items-center justify-center">
                <div className="relative inline-flex items-center justify-center mb-8">
                  <i className="pi pi-file text-8xl" />
                  <i className="pi pi-ban absolute text-red-500 text-9xl" />
                </div>
                <p className="text-4xl">No Curriculum</p>
              </div>
            )}
            {modalInfo?.type === 'cv' && modalUrl && (
              <PreviewFile providedUrl={modalUrl} hideBanner />
            )}
            {modalInfo?.type === 'spk' && modalUrl && (
              <SimeraIframe iframeUrl={modalUrl} title="SPK Viewer" />
            )}
          </div>
        </div>
      </ModalInTailwind>
    );
  }
);

export default SearchCandidateModal;
